import { useCallback, useEffect, useState } from "react";
import { FileText, Loader2, Share2 } from "lucide-react";
import { toast } from "react-toastify";
import { DOCUMENT_LIST_REPORT_PDF_VIEWER_HASH } from "@/services/tradingReports";
import { Button } from "@/components/ui/button";
import PdfJsCanvasViewer from "./PdfJsCanvasViewer";
import { downloadBlobUrl, sharePdfFromBlobUrl } from "./pdfDownload";

/**
 * نمایش PDF در iframe با پارامترهای پرکاربرد نمای ابزار Acrobat/مرورگر
 * (هم‌راستا با aryazar_gitea)
 */
const VIEWER_FRAGMENT = "toolbar=1&navpanes=0&scrollbar=1&view=FitH";

function isIosLike() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

function showMobileDownloadFallback() {
  if (isIosLike()) return true;
  if (typeof navigator === "undefined") return false;
  if (/Android/i.test(navigator.userAgent || "")) return true;
  if (/(webOS|BlackBerry|IEMobile|Opera Mini)/i.test(navigator.userAgent || "")) return true;
  return false;
}

function appendViewerFragment(trimmedUrl) {
  const lower = trimmedUrl.toLowerCase();
  if (lower.startsWith("blob:") || lower.startsWith("data:")) {
    return trimmedUrl;
  }

  const hashIdx = trimmedUrl.indexOf("#");
  if (hashIdx === -1) {
    return `${trimmedUrl}#${VIEWER_FRAGMENT}`;
  }

  const frag = trimmedUrl.slice(hashIdx + 1);
  const fragLc = frag.toLowerCase();
  if (fragLc.includes("toolbar=")) {
    return trimmedUrl;
  }
  return `${trimmedUrl}&${VIEWER_FRAGMENT}`;
}

function urlForExternalPdfOpen(trimmed) {
  if (trimmed.endsWith(DOCUMENT_LIST_REPORT_PDF_VIEWER_HASH)) {
    return trimmed.slice(0, -DOCUMENT_LIST_REPORT_PDF_VIEWER_HASH.length);
  }
  return trimmed;
}

function isBlobOrDataUrl(url) {
  return /^blob:|^data:/i.test(String(url || ""));
}

function canUseWebShareApi() {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

function PdfDownloadFooter({ sourceUrl, fileName, standalone = false }) {
  const [busy, setBusy] = useState(null);
  const showShare = canUseWebShareApi();

  const outer = standalone
    ? "w-full"
    : "shrink-0 border-t border-border bg-muted/40 px-3 py-3 sm:px-4";

  const onDownload = useCallback(() => {
    if (!sourceUrl || busy) return;
    try {
      downloadBlobUrl(sourceUrl, fileName);
    } catch {
      toast.error("دانلود فایل انجام نشد. لطفاً دوباره تلاش کنید.");
    }
  }, [sourceUrl, fileName, busy]);

  const onShare = useCallback(async () => {
    if (!sourceUrl || busy) return;
    setBusy("share");
    try {
      const shared = await sharePdfFromBlobUrl(sourceUrl, fileName);
      if (!shared) {
        toast.info("اشتراک‌گذاری فایل در این مرورگر پشتیبانی نمی‌شود. فایل را دانلود کنید.");
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      toast.error("اشتراک‌گذاری فایل انجام نشد. لطفاً دوباره تلاش کنید.");
    } finally {
      setBusy(null);
    }
  }, [sourceUrl, fileName, busy]);

  return (
    <div className={outer}>
      <div className="flex flex-col gap-3 rounded-xl border border-border/70 bg-card p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-4">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
            aria-hidden
          >
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 text-right">
            <p className="text-sm font-semibold leading-tight text-foreground">فایل گزارش</p>
            <p className="truncate text-xs text-muted-foreground" title={fileName}>
              {fileName}
            </p>
          </div>
        </div>
        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
          {showShare ? (
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="h-11 w-full sm:w-auto sm:min-w-[9rem]"
              disabled={!!busy || !sourceUrl}
              onClick={onShare}
            >
              {busy === "share" ? (
                <Loader2 className="ml-2 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Share2 className="ml-2 h-4 w-4" aria-hidden />
              )}
              اشتراک‌گذاری
            </Button>
          ) : null}
          <Button
            type="button"
            size="lg"
            className="h-11 w-full sm:w-auto sm:min-w-[9rem]"
            disabled={!!busy || !sourceUrl}
            onClick={onDownload}
          >
            دانلود گزارش
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PdfIframeViewer({
  fileUrl,
  title = "PDF Viewer",
  downloadFileName = "gozaresh-list-asnad.pdf",
}) {
  const trimmed = fileUrl ? String(fileUrl).trim() : "";
  const iframeSrc = trimmed ? appendViewerFragment(trimmed) : "";
  const [canvasFailed, setCanvasFailed] = useState(false);
  const onCanvasError = useCallback(() => setCanvasFailed(true), []);

  useEffect(() => {
    setCanvasFailed(false);
  }, [trimmed]);

  if (!iframeSrc) {
    return (
      <div className="flex h-full min-h-[420px] w-full items-center justify-center rounded-md border bg-white p-4 text-sm text-muted-foreground">
        آدرس فایل PDF معتبر نیست.
      </div>
    );
  }

  const downloadSource = urlForExternalPdfOpen(trimmed);
  const isMobile = showMobileDownloadFallback();
  const isBlob = isBlobOrDataUrl(trimmed);
  const usePdfJs = isMobile && isBlob && !canvasFailed;

  if (isMobile && isBlob && canvasFailed) {
    return (
      <div className="flex min-h-[min(200px,40dvh)] w-full flex-1 flex-col justify-center rounded-md border bg-white p-4">
        <PdfDownloadFooter sourceUrl={downloadSource} fileName={downloadFileName} standalone />
      </div>
    );
  }

  if (usePdfJs) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-md border bg-white">
        <div className="relative flex min-h-[min(240px,50dvh)] flex-1 flex-col overflow-hidden">
          <PdfJsCanvasViewer fileUrl={trimmed} onLoadError={onCanvasError} />
        </div>
        <PdfDownloadFooter sourceUrl={downloadSource} fileName={downloadFileName} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-md border bg-white">
      <div className="relative min-h-[min(240px,50dvh)] flex-1 overflow-hidden">
        <iframe title={title} src={iframeSrc} className="absolute inset-0 h-full w-full border-0" />
      </div>
      {isMobile ? (
        <PdfDownloadFooter sourceUrl={downloadSource} fileName={downloadFileName} />
      ) : null}
    </div>
  );
}
